/**
 * Product Popup Module
 * Handles the product add to cart popup functionality
 * Can be triggered by external apps via window.ProductPopup API
 */

import Swiper, { Navigation, Thumbs, Keyboard } from "swiper";

class ProductPopup extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.currentProduct = null;
    this.currentVariant = null;
    this.isDragging = false;

    // Bind methods
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleBackdropClick = this.handleBackdropClick.bind(this);
    this.handleFormSubmit = this.handleFormSubmit.bind(this);

    // Initialize elements
    this.initializeElements();
    this.attachEventListeners();
  }

  initializeElements() {
    this.popup = this;
    this.closeBtn = this.querySelector('[data-action="close"]');
    this.form = this.querySelector("#popup-product-form");
    this.loadingEl = this.querySelector("#popup-loading");
    this.detailsEl = this.querySelector("#popup-product-details");
    this.contentEl = this.querySelector("#popup-product-content");
    this.errorEl = this.querySelector("#popup-error");
    this.retryBtn = this.querySelector('[data-action="retry"]');
    this.wishListBtn = this.querySelector(".wishlist-button-reset");

    // Swiper elements
    this.mainSliderEl = this.querySelector("#popup-main-slider");
    this.thumbsSliderEl = this.querySelector("#popup-thumbs-slider");

    // Product elements
    this.titleEl = this.querySelector("#popup-product-title");
    this.hiddenSelectedMaterial = this.querySelector(
      "#popup-selected-material"
    );
    this.priceEl = this.querySelector("#popup-product-price");
    this.priceRangeEl = this.querySelector("#popup-product-price-range");
    this.optionsEl = this.querySelector("#popup-product-options");
    this.variantIdInput = this.querySelector("#popup-variant-id");
    this.quantityInput = this.querySelector("#popup-quantity");
    this.addToCartBtn = this.querySelector("#popup-add-to-cart");
    this.detailsLink = this.querySelector(".btn-secondary");
    this.generalLink = this.querySelector(".abtext-c");
    this.aufAnfrageFormEl = this.querySelector("#popup-auf-anfrage-form");
    this.norestockWrap = this.querySelector("#popup-norestock-link-wrap");
    this.norestockLink = this.querySelector("#popup-norestock-link");

    // Swiper instances
    this.mainSwiper = null;
    this.thumbsSwiper = null;
    this.productImages = [];
    this.productMedia = [];
  }

  attachEventListeners() {
    // Close button
    this.closeBtn?.addEventListener("click", () => this.close());

    // Backdrop click
    this.addEventListener("click", this.handleBackdropClick);

    // Form submission
    this.form?.addEventListener("submit", this.handleFormSubmit);

    // Retry button
    this.retryBtn?.addEventListener("click", () => {
      if (this.currentProduct) {
        this.loadProduct(this.currentProduct);
      }
    });
  }

  handleKeyDown(e) {
    if (e.key === "Escape") {
      this.close();
    }
  }

  /**
   * Back in stock: send email to Klaviyo and update form for customer feedback.
   * Adapted from product-form.js for the popup structure (variant from #popup-variant-id).
   */
  backInStock(event) {
    event.preventDefault();
    console.log("backInStock");
    const emailField = this.querySelector("#backinstockemail");
    const errorMessage = this.querySelector("#backinstockemail + .hidden");
    const button = this.querySelector("#backinstockbutton");
    const email = emailField?.value?.trim() ?? "";
    const variant = this.querySelector("#popup-variant-id")?.value ?? "";

    if (!email) {
      errorMessage?.classList.remove("hidden");
      return;
    } else {
      errorMessage?.classList.add("hidden");
    }

    const payload = {
      data: {
        type: "back-in-stock-subscription",
        attributes: {
          profile: {
            data: {
              type: "profile",
              attributes: {
                email: email,
              },
            },
          },
          channels: ["EMAIL"],
        },
        relationships: {
          variant: {
            data: {
              type: "catalog-variant",
              id: "$shopify:::$default:::" + variant,
            },
          },
        },
      },
    };

    const requestOptions = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        revision: "2023-12-15",
      },
      body: JSON.stringify(payload),
    };

    fetch(
      "https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=VhnDTq",
      requestOptions
    ).then((response) => {
      if (response.status === 202) {
        errorMessage?.classList.add("hidden");
        emailField?.classList.add("send", "hidden");
        button?.classList.remove("btn-primary", "text-anna-gray");
        button?.classList.add(
          "bg-white",
          "border",
          "border-black",
          "cursor-default",
          "text-anna-black"
        );
        button?.setAttribute("onclick", "event.preventDefault();");
        button?.setAttribute("disabled", "");
        const translations = window.POPUP_TRANSLATIONS || {};
        button.textContent =
          translations.notificationActive ||
          (window.location.pathname.includes("/en")
            ? "Notification active"
            : "Benachrichtigung aktiv");
      }
      if (response.status === 400) {
        errorMessage?.classList.remove("hidden");
      }
    });
  }

  handleBackdropClick(e) {
    if (e.target === this && !this.isDragging) {
      this.close();
    }
    // Reset dragging flag after check
    this.isDragging = false;
  }

  async handleFormSubmit(e) {
    e.preventDefault();

    if (!this.currentVariant) {
      console.error("[ProductPopup] No variant selected");
      return;
    }

    const translations = window.POPUP_TRANSLATIONS || {};
    const originalText = this.addToCartBtn.textContent;
    const cart = document.querySelector("shopify-cart");

    this.addToCartBtn.disabled = true;

    const popupCartDrawerDelayMs = 400;

    try {
      // Only collect form data from the add-to-cart form (product options)
      // Exclude any email fields from back-in-stock or auf-anfrage forms
      const formData = new FormData();
      formData.append("id", this.variantIdInput.value);
      formData.append("quantity", this.quantityInput.value || "1");

      if (cart) {
        // Use existing cart functionality (desktop: delay flyout so success state shows first)
        const isMobile = window.matchMedia("(max-width: 767px)").matches;
        await cart.addItemFromForm(this.form, {
          delayDrawerMs: isMobile ? 0 : popupCartDrawerDelayMs,
        });
        if (isMobile) {
          // Mobile: show secondary style + success text for 2 seconds
          this.addToCartBtn.classList.remove("btn-primary");
          this.addToCartBtn.classList.add("btn-secondary", "popup-add-success");
          this.addToCartBtn.textContent =
            translations.itemAdded || "Item added to your cart";
          this.addToCartBtn.disabled = true;

          setTimeout(() => {
            this.addToCartBtn.classList.remove(
              "btn-secondary",
              "popup-add-success"
            );
            this.addToCartBtn.classList.add("btn-primary");
            this.updateAddToCartButton();
          }, 2000);
        } else {
          // Desktop: success text, then flyout opens after popupCartDrawerDelayMs; close when it starts
          this.addToCartBtn.classList.remove("btn-primary");
          this.addToCartBtn.classList.add("btn-secondary", "popup-add-success");
          this.addToCartBtn.textContent =
            translations.itemAdded || "Item added to your cart";
          this.addToCartBtn.disabled = true;

          await new Promise((resolve) => {
            let settled = false;
            const done = () => {
              if (settled) return;
              settled = true;
              window.removeEventListener("cart:drawer-opened", onDrawerOpened);
              clearTimeout(fallbackId);
              resolve();
            };
            const onDrawerOpened = () => done();
            window.addEventListener("cart:drawer-opened", onDrawerOpened, {
              once: true,
            });
            const fallbackId = setTimeout(done, popupCartDrawerDelayMs + 200);
          });
          this.close();
        }
      } else {
        // Fallback to direct API call (popup closes)
        await this.addToCartDirect(formData);
      }
    } catch (error) {
      console.error("[ProductPopup] Failed to add to cart:", error);
      this.addToCartBtn.disabled = false;
      this.addToCartBtn.textContent = originalText;
      this.addToCartBtn.classList.remove("btn-secondary");
      this.addToCartBtn.classList.add("btn-primary");
    }
  }

  async addToCartDirect(formData) {
    const translations = window.POPUP_TRANSLATIONS || {};

    const response = await fetch("/cart/add.js", {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      throw new Error("Failed to add to cart");
    }

    await response.json();
    //console.log("[ProductPopup] Added to cart:", result);

    this.addToCartBtn.classList.remove("btn-primary");
    this.addToCartBtn.classList.add("btn-secondary", "popup-add-success");
    this.addToCartBtn.textContent =
      translations.itemAdded || "Item added to your cart";
    this.addToCartBtn.disabled = true;

    await new Promise((resolve) => setTimeout(resolve, 500));
    this.close();

    // Update cart count if available
    this.updateCartCount();
  }

  async updateCartCount() {
    try {
      const response = await fetch("/cart.js");
      const cart = await response.json();

      // Dispatch custom event with cart data
      window.dispatchEvent(
        new CustomEvent("cart:updated", {
          detail: { cart },
        })
      );
    } catch (error) {
      console.error("[ProductPopup] Failed to update cart count:", error);
    }
  }

  async loadProduct(productHandle, variantId = null) {
    // Reset all state before loading new product
    this.resetProductState();

    this.currentProduct = productHandle;
    this.presetVariantId = variantId;
    this.showLoading();

    const localeRoot =
      (typeof window !== "undefined" && window.Shopify.routes.root) || "";
    const productBase = `${localeRoot}products/${productHandle}`;

    try {
      // Fetch both product JSON and metafield data in parallel
      const [productResponse, metaResponse] = await Promise.all([
        fetch(`${productBase}.js`),
        fetch(`${productBase}?section_id=product-popup-data`),
      ]);

      if (!productResponse.ok) {
        throw new Error("Product not found");
      }

      const product = await productResponse.json();
      const metaDataText = await metaResponse.text();

      // Remove Shopify section wrapper div if present
      let jsonText = metaDataText.trim();
      const divMatch = jsonText.match(/<div[^>]*>([\s\S]*)<\/div>/);
      if (divMatch) {
        jsonText = divMatch[1].trim();
      }

      // Parse JSON from section
      const metaData = JSON.parse(jsonText);

      //console.log("[ProductPopup] Loaded product:", product);
      //console.log("[ProductPopup] Loaded metafield data:", metaData);

      this.renderProduct(product, metaData);
    } catch (error) {
      console.error("[ProductPopup] Failed to load product:", error);
      this.showError();
    }
  }

  resetProductState() {
    // Clear all product-specific state
    this.currentProduct = null;
    this.currentVariant = null;
    this.presetVariantId = null;
    this.metaData = null;
    this.productImages = [];
    this.productMedia = [];
    this.quantityInput.value = 1;

    // Clear options HTML
    if (this.optionsEl) {
      this.optionsEl.innerHTML = "";
    }

    // Clear dynamic swatch styles
    document
      .querySelectorAll('[id^="popup-option-"][id$="-swatch-style"]')
      .forEach((el) => el.remove());

    // Clear previous content
    if (this.titleEl) this.titleEl.textContent = "";
    if (this.priceEl) this.priceEl.textContent = "";
    if (this.variantIdInput) this.variantIdInput.value = "";

    // Reset back in stock form when switching products
    this.resetBackInStockForm();

    if (this.addToCartBtn) {
      this.addToCartBtn.classList.remove("popup-add-success", "btn-secondary");
      this.addToCartBtn.classList.add("btn-primary");
      this.addToCartBtn.style.display = "";
    }
  }

  resetBackInStockForm() {
    const emailField = this.querySelector("#backinstockemail");
    const errorMessage = this.querySelector("#backinstockemail + .hidden");
    const button = this.querySelector("#backinstockbutton");
    const translations = window.POPUP_TRANSLATIONS || {};
    if (emailField) {
      emailField.value = "";
      emailField.classList.remove("send", "hidden");
    }
    errorMessage?.classList.add("hidden");
    if (button) {
      button.classList.remove(
        "bg-white",
        "border",
        "border-black",
        "cursor-default",
        "text-anna-black"
      );
      button.classList.add("btn-primary", "text-anna-white");
      button.removeAttribute("disabled");
      button.textContent =
        translations.backinstockButtonTextPopup || "GET NOTIFIED";
    }
  }

  renderProduct(product, metaData) {
    // Store product media (images and videos) and metafield data
    this.productMedia = product.media || [];
    this.productImages = product.images || [];
    this.metaData = metaData;

    // Set product details
    this.titleEl.textContent = product.title.toLowerCase();

    // Find preset variant, or first available and visible variant
    let defaultVariant = product.variants[0];

    // Check if a preset variant ID was provided
    if (this.presetVariantId) {
      const presetVariant = product.variants.find(
        (v) => v.id === this.presetVariantId
      );
      if (presetVariant) {
        defaultVariant = presetVariant;
        console.log(
          "[ProductPopup] Using preset variant:",
          this.presetVariantId
        );
      } else {
        console.warn(
          "[ProductPopup] Preset variant ID not found:",
          this.presetVariantId
        );
      }
      this.presetVariantId = null; // Clear after use
    } else if (metaData && metaData.variants) {
      // Fall back to first available variant
      const firstAvailable = metaData.variants.find(
        (v) => !v.disabled && v.available
      );
      if (firstAvailable) {
        defaultVariant =
          product.variants.find((v) => v.id === firstAvailable.id) ||
          defaultVariant;
      }
    }

    this.currentVariant = defaultVariant;
    this.variantIdInput.value = defaultVariant.id;

    // Update wishlist button data attributes
    if (!this.metaData?.is_customizable) {
      this.wishListBtn.dataset.productHandle = product.handle;
      this.wishListBtn.dataset.variantId = defaultVariant.id;
      this.wishListBtn.style.display = "block";
    } else {
      this.wishListBtn.style.display = "none";
    }

    // Update details link with variant
    this.updateDetailsLink(product.handle, defaultVariant.id);

    // Update price display
    this.updatePriceDisplay(defaultVariant.price);

    // Update price range display
    this.updatePriceRangeDisplay(product);

    // Initialize Swiper sliders
    this.initializeSwiper(product);

    this.renderVariants(product);

    // Set initial material/farbe value in hidden span
    const materialOrFarbeSelect = Array.from(
      this.optionsEl.querySelectorAll("select")
    ).find((select) => {
      const optionName = select.dataset.optionName.toLowerCase();
      return (
        optionName.includes("material") ||
        optionName.includes("farbe") ||
        optionName.includes("color") ||
        optionName.includes("colour")
      );
    });

    if (materialOrFarbeSelect) {
      this.hiddenSelectedMaterial.classList.remove("!hidden");
      this.hiddenSelectedMaterial.textContent = materialOrFarbeSelect.value;
    } else {
      this.hiddenSelectedMaterial.classList.add("!hidden");
    }

    // Go to variant's featured image if it exists
    if (defaultVariant.featured_image) {
      this.goToVariantImage(defaultVariant.featured_image);
    }

    // Update availability display
    this.updateAvailability();

    // Update add to cart button state
    this.updateAddToCartButton();

    this.showDetails();
  }

  renderVariants(product) {
    // Clear all previous options and their styles
    this.optionsEl.innerHTML = "";

    // Clean up any previous swatch styles from previous product
    document
      .querySelectorAll('[id^="popup-option-"][id$="-swatch-style"]')
      .forEach((el) => el.remove());

    // Group variants by options
    if (product.options && product.options.length > 0) {
      product.options.forEach((option, index) => {
        const optionContainer = document.createElement("div");
        optionContainer.className =
          "flex-col mb-[30px] lg:mb-[28px] last:mb-0 flex";

        const lowerName = option.name.toLowerCase();
        //console.log("[ProductPopup] Rendering option:", lowerName);
        const isMaterial = lowerName.includes("material");
        const isFarbe =
          lowerName.includes("farbe") ||
          lowerName.includes("color") ||
          lowerName.includes("colour");
        const isNumerology = this.metaData?.is_numerology;
        if (isNumerology && lowerName.includes("zahl")) {
          optionContainer.className =
            "flex-col mb-[30px] lg:mb-[28px] last:mb-0 hidden";
        }

        // Determine label text from translations
        const translations = window.POPUP_TRANSLATIONS || {};
        let labelText = option.name;
        //console.log("[ProductPopup] Rendering option:", lowerName);
        //console.log("[ProductPopup] isMaterial:", isMaterial);
        //console.log("[ProductPopup] Translations:", translations);
        if (isMaterial && translations.chooseMaterial) {
          labelText = translations.chooseMaterial;
        } else if (isFarbe && translations.chooseColor) {
          labelText = translations.chooseColor;
        }

        const label = document.createElement("label");
        label.className =
          "block lg:mb-1 text-anna-black text-mobile-sm md:text-sm hidden md:block";
        label.textContent = labelText;
        label.htmlFor = `popup-option-${index}`;

        const select = document.createElement("select");
        const selectClass = isFarbe || isMaterial ? "px-8" : "px-0";
        select.className = `${selectClass} border-0 text-mobile-sm md:text-sm text-anna-black py-[6px] border-b border-anna-gray focus:outline-none focus-visible:shadow-none focus-visible:outline-none focus:shadow-none focus:border-b focus:border-anna-gray focus:shadow-transparent focus:ring-0 popup-select`;
        select.dataset.optionIndex = index;
        select.dataset.optionName = option.name;
        select.id = `popup-option-${index}`;

        // Add option values (filter out disabled ones)
        const optionMeta = this.metaData?.options?.find(
          (o) => o.name === option.name
        );
        const availableValues = optionMeta?.available_values || option.values;

        availableValues.forEach((value, valueIndex) => {
          const optionEl = document.createElement("option");
          optionEl.value = value;
          optionEl.textContent = value;
          // Select the option that matches current variant
          if (value === this.currentVariant.options[index]) {
            optionEl.selected = true;
          }
          select.appendChild(optionEl);
        });

        // Handle option change
        select.addEventListener("change", () => {
          this.updateSelectedVariant(product);
          // Update swatch styling when selection changes
          if (isMaterial || isFarbe) {
            this.updateSwatchStyling(
              select,
              option.values,
              isMaterial,
              isFarbe
            );
          }
        });

        // Add click handler for open/close styling
        select.addEventListener("click", () => {
          select.classList.toggle("open");
        });

        select.addEventListener("blur", () => {
          select.classList.remove("open");
        });

        optionContainer.appendChild(label);
        optionContainer.appendChild(select);
        this.optionsEl.appendChild(optionContainer);

        // Apply initial swatch styling for Material/Farbe options
        if (isMaterial || isFarbe) {
          this.updateSwatchStyling(select, option.values, isMaterial, isFarbe);
        }
      });
    }
  }

  updateSwatchStyling(selectElement, optionValues, isMaterial, isFarbe) {
    const swatchColors = window.POPUP_SWATCH_COLORS || [];
    const selectedValue = selectElement.value;

    // Remove existing style element for this select
    const existingStyle = document.querySelector(
      `#${selectElement.id}-swatch-style`
    );
    if (existingStyle) {
      existingStyle.remove();
    }

    // Find matching color for selected value
    let swatchBgColor = null;
    let isWhite = false;
    const searchColor = selectedValue.toLowerCase().replace(/\s/g, "");

    for (const swatchOption of swatchColors) {
      const parts = swatchOption.split(":");
      if (parts.length !== 2) continue;

      const colorName = parts[0].replace(/\s/g, "").toLowerCase();
      const colorValue = parts[1].trim().replace("#", "");

      if (searchColor === colorName) {
        swatchBgColor = colorValue;
        break;
      }
    }

    // Special case for "schwarz" in Material
    if (searchColor.includes("schwarz") && isMaterial) {
      swatchBgColor = "423F40";
    }

    // Check if color is white/weiß
    if (searchColor.includes("weiß") && isFarbe) {
      isWhite = true;
    }

    // Create dynamic style for this select
    if (swatchBgColor || isMaterial || isFarbe) {
      const styleEl = document.createElement("style");
      styleEl.id = `${selectElement.id}-swatch-style`;

      const circleSize = isWhite ? "16" : "18";
      const circleStroke = isWhite ? "stroke='black'" : "";
      const positionX = isWhite ? "3px" : "2px";
      const positionY = "6px";
      const positionYMd = isWhite ? "7.5px" : "7px";

      let css = "";

      if (swatchBgColor) {
        css = `
          select#${selectElement.id} {
            background: url("data:image/svg+xml,%3Csvg width='${circleSize}' height='${circleSize}' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='10' cy='10' r='9' ${circleStroke} fill='%23${swatchBgColor}'/%3E%3C/svg%3E%0A") no-repeat ${positionX} ${positionY},
            url("data:image/svg+xml,%3Csvg width='11' height='11' viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cline x1='1' y1='6' x2='11' y2='6' stroke='%23111111' stroke-linecap='round'/%3E%3Cline x1='6' y1='1' x2='6' y2='11' stroke='%23111111' stroke-linecap='round'/%3E%3C/svg%3E%0A") no-repeat 100% 10px;
          }
          select#${selectElement.id}.open {
            background: url("data:image/svg+xml,%3Csvg width='${circleSize}' height='${circleSize}' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='10' cy='10' r='9' ${circleStroke} fill='%23${swatchBgColor}'/%3E%3C/svg%3E%0A") no-repeat ${positionX} ${positionY},
            url("data:image/svg+xml,%3Csvg width='11' height='11' viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cline x1='1' y1='6' x2='11' y2='6' stroke='%23111111' stroke-linecap='round'/%3E%3C/svg%3E%0A") no-repeat 100% 10px;
          }
          @media screen and (min-width: 768px) {
            select#${selectElement.id} {
              background-position: ${positionX} ${positionYMd}, 100% 10px;
            }
            select#${selectElement.id}.open {
              background-position: ${positionX} ${positionYMd}, 100% 10px;
            }
          }
        `;
      }

      styleEl.textContent = css;
      document.head.appendChild(styleEl);
    }
  }

  updateSelectedVariant(product) {
    const selectedOptions = Array.from(
      this.optionsEl.querySelectorAll("select")
    ).map((select) => select.value);

    // Find matching variant
    const variant = product.variants.find((v) => {
      return v.options.every(
        (option, index) => option === selectedOptions[index]
      );
    });

    if (variant) {
      this.currentVariant = variant;
      this.variantIdInput.value = variant.id;

      // Update wishlist button
      this.wishListBtn.dataset.variantId = variant.id;

      // Update details link with new variant
      this.updateDetailsLink(product.handle, variant.id);

      // Update price
      this.updatePriceDisplay(variant.price);

      // Update slider if variant has a specific image
      if (variant.featured_image && this.mainSwiper) {
        this.goToVariantImage(variant.featured_image);
      }

      // Update availability display
      this.updateAvailability();

      this.updateAddToCartButton();
    }
  }

  /**
   * Builds a Shopify CDN image URL with width/height (and optional crop).
   * @param {string} baseUrl - Base image URL (e.g. from product media)
   * @param {number} width
   * @param {number} height
   * @param {string} [crop]
   * @returns {string}
   */
  imageUrlWithSize(baseUrl, width, height, crop) {
    if (typeof baseUrl !== "string") return baseUrl;
    const sep = baseUrl.includes("?") ? "&" : "?";
    let url = `${baseUrl}${sep}width=${width}&height=${height}`;
    if (crop) url += `&crop=${crop}`;
    return url;
  }

  /**
   * Creates a <picture> with 1x fallback <img> and 2 <source> elements for retina (2x, 3x).
   * Similar to responsive-image.liquid but minimal: standard img + 2 higher-res sources.
   */
  createPictureElement(baseUrl, size, alt, loading = "lazy") {
    const { width, height, crop } = {
      width: size.width,
      height: size.height,
      crop: size.crop,
    };
    const picture = document.createElement("picture");
    picture.className = "block w-full h-full";
    const retinaMedia2x =
      "(min-resolution: 2dppx), (-webkit-min-device-pixel-ratio: 2)";
    const retinaMedia3x =
      "(min-resolution: 3dppx), (-webkit-min-device-pixel-ratio: 3)";

    const src1x = this.imageUrlWithSize(baseUrl, width, height, crop);
    const src2x = this.imageUrlWithSize(baseUrl, width * 2, height * 2, crop);
    const src3x = this.imageUrlWithSize(baseUrl, width * 3, height * 3, crop);

    const source3x = document.createElement("source");
    source3x.srcset = src3x;
    source3x.media = retinaMedia3x;
    picture.appendChild(source3x);

    const source2x = document.createElement("source");
    source2x.srcset = src2x;
    source2x.media = retinaMedia2x;
    picture.appendChild(source2x);

    const img = document.createElement("img");
    img.src = src1x;
    img.alt = alt;
    img.loading = loading;
    img.className = "w-full h-full object-cover";
    picture.appendChild(img);

    return picture;
  }

  initializeSwiper(product) {
    // Use media if available, fallback to images
    const media =
      this.productMedia.length > 0
        ? this.productMedia
        : this.productImages.map((src) => ({ media_type: "image", src }));

    if (media.length === 0) {
      //console.log("[ProductPopup] No media, hiding sliders");
      this.mainSliderEl.style.display = "none";
      this.thumbsSliderEl.parentElement.style.display = "none";
      return;
    }

    if (!this.mainSliderEl || !this.thumbsSliderEl) {
      console.error("[ProductPopup] Slider elements not found");
      return;
    }

    // Destroy existing Swiper instances to clear old content
    if (this.mainSwiper) {
      this.mainSwiper.destroy(true, true);
      this.mainSwiper = null;
    }
    if (this.thumbsSwiper) {
      this.thumbsSwiper.destroy(true, true);
      this.thumbsSwiper = null;
    }

    // Show sliders
    this.mainSliderEl.style.display = "block";
    this.thumbsSliderEl.parentElement.style.display = "block";

    // Populate main slider
    const mainWrapper = this.mainSliderEl.querySelector(".swiper-wrapper");
    mainWrapper.innerHTML = "";

    media.forEach((item, index) => {
      const slide = document.createElement("div");
      slide.className = "swiper-slide shrink-0";

      if (item.media_type === "video" || item.media_type === "external_video") {
        const videoContainer = document.createElement("div");
        videoContainer.className = "w-full h-full relative";

        if (item.media_type === "external_video") {
          // Handle external videos (YouTube, Vimeo)
          const iframe = document.createElement("iframe");
          iframe.src = item.sources?.[0]?.url || item.external_id;
          iframe.className = "w-full h-full object-cover";
          iframe.allow = "autoplay; encrypted-media";
          iframe.allowFullscreen = true;
          videoContainer.appendChild(iframe);
        } else {
          // Handle native videos - autoplay, loop, muted like background videos
          const video = document.createElement("video");
          video.autoplay = true;
          video.loop = true;
          video.muted = true;
          video.playsInline = true;
          video.className = "w-full h-full object-cover";
          video.poster = item.preview_image?.src;

          if (item.sources) {
            item.sources.forEach((source) => {
              const sourceEl = document.createElement("source");
              sourceEl.src = source.url;
              sourceEl.type = source.mime_type;
              video.appendChild(sourceEl);
            });
          }
          videoContainer.appendChild(video);
        }
        slide.appendChild(videoContainer);
      } else {
        // Handle images: picture with 1x fallback + 2 sources for retina (2x, 3x)
        const imgSrc = item.src || item;
        const baseUrl = typeof imgSrc === "string" ? imgSrc : imgSrc;
        const picture = this.createPictureElement(
          baseUrl,
          { width: 335, height: 419 },
          product.title,
          index === 0 ? "eager" : "lazy"
        );

        // Check if image is variant-attached by comparing with variant featured images
        const isVariantAttached = product.variants.some(
          (variant) => variant.featured_image?.src === item.src
        );
        const img = picture.querySelector("img");
        if (item.aspect_ratio === 1 && isVariantAttached) {
          img.className = "w-full h-full object-contain";
        } else {
          img.className = "w-full h-full object-cover";
        }
        slide.appendChild(picture);
      }

      mainWrapper.appendChild(slide);
    });

    // Populate thumbnails slider
    const thumbsWrapper = this.thumbsSliderEl.querySelector(".swiper-wrapper");
    thumbsWrapper.innerHTML = "";

    media.forEach((item, index) => {
      const slide = document.createElement("div");
      slide.className = "swiper-slide cursor-pointer shrink-0 relative";

      // Get preview image for thumbnail
      let thumbSrc;
      if (item.media_type === "video" || item.media_type === "external_video") {
        thumbSrc = item.preview_image?.src || item.preview_image;
      } else {
        thumbSrc = item.src || item;
      }

      const baseUrl = typeof thumbSrc === "string" ? thumbSrc : thumbSrc;
      // Freisteller: full product on white — don't crop CDN or thumb; contain in slot.
      // Tagebild / others: center-crop thumb + object-cover.
      const isFreistellerThumb =
        typeof baseUrl === "string" && /Freisteller/i.test(baseUrl);
      const picture = this.createPictureElement(
        baseUrl,
        isFreistellerThumb
          ? { width: 64, height: 80 }
          : { width: 64, height: 80, crop: "center" },
        product.title,
        "lazy"
      );
      const img = picture.querySelector("img");
      img.className = isFreistellerThumb
        ? "w-thumbs h-thumbs object-contain"
        : "w-thumbs h-thumbs object-cover";
      slide.appendChild(picture);

      // Add video play icon overlay for video thumbnails
      if (item.media_type === "video" || item.media_type === "external_video") {
        const playIcon = document.createElement("div");
        playIcon.className =
          "absolute inset-0 flex items-center justify-center pointer-events-none";
        playIcon.innerHTML = `
          <svg class="w-6 h-6 text-white drop-shadow-lg" fill="currentColor" viewBox="0 0 20 20">
            <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z"/>
          </svg>
        `;
        slide.appendChild(playIcon);
      }

      thumbsWrapper.appendChild(slide);
    });

    // Initialize thumbnails swiper first
    this.thumbsSwiper = new Swiper(this.thumbsSliderEl, {
      slidesPerView: "auto",
      spaceBetween: 6,
      freeMode: true,
      watchSlidesProgress: true,
      on: {
        init: () => {
          this.updateThumbsFade();
        },
        slideChange: () => {
          this.updateThumbsFade();
        },
        resize: () => {
          this.updateThumbsFade();
        },
        touchStart: () => {
          this.isDragging = true;
        },
        touchEnd: () => {
          // Keep dragging flag true momentarily to catch backdrop click
          setTimeout(() => {
            this.isDragging = false;
          }, 50);
        },
      },
    });

    // Initialize main swiper
    this.mainSwiper = new Swiper(this.mainSliderEl, {
      modules: [Navigation, Thumbs, Keyboard],
      slidesPerView: 1,
      spaceBetween: 0,
      navigation: {
        nextEl: ".swiper-button-next",
        prevEl: ".swiper-button-prev",
      },
      keyboard: {
        enabled: true,
        onlyInViewport: true,
      },
      thumbs: {
        swiper: this.thumbsSwiper,
      },
      on: {
        touchStart: () => {
          this.isDragging = true;
        },
        touchEnd: () => {
          // Keep dragging flag true momentarily to catch backdrop click
          setTimeout(() => {
            this.isDragging = false;
          }, 50);
        },
        slideChange: (swiper) => {
          // Play video on active slide, pause others
          this.handleVideoPlayback(swiper);
          this.updateOverlayIconState(swiper);
        },
        init: (swiper) => {
          // Play video on initial slide
          this.handleVideoPlayback(swiper);
          this.updateOverlayIconState(swiper);
        },
      },
    });
  }

  handleVideoPlayback(swiper) {
    // Pause all videos
    const allVideos = this.mainSliderEl.querySelectorAll("video");
    allVideos.forEach((video) => {
      video.pause();
    });

    // Play video on active slide
    const activeSlide = swiper.slides[swiper.activeIndex];
    if (activeSlide) {
      const video = activeSlide.querySelector("video");
      if (video) {
        video.play().catch((err) => {
          //console.log("[ProductPopup] Video autoplay prevented:", err);
        });
      }
    }
  }

  /**
   * Toggle light overlay icons (white) when current slide image has "Tragefoto" in URL.
   * Wishlist icon: always; close button: mobile only (via CSS).
   */
  updateOverlayIconState(swiper) {
    if (!swiper?.slides?.length) {
      this.classList.remove("popup-gallery-light-overlay");
      return;
    }
    const activeSlide = swiper.slides[swiper.activeIndex];
    if (!activeSlide) {
      this.classList.remove("popup-gallery-light-overlay");
      return;
    }
    const img = activeSlide.querySelector("img");
    const video = activeSlide.querySelector("video");
    let src = "";
    if (img?.src) src = img.src;
    const isLightOverlay =
      (typeof src === "string" && src.includes("Tragefoto")) || video;
    if (isLightOverlay) {
      this.classList.add("popup-gallery-light-overlay");
    } else {
      this.classList.remove("popup-gallery-light-overlay");
    }
  }

  goToSlide(index) {
    const mediaLength =
      this.productMedia.length > 0
        ? this.productMedia.length
        : this.productImages.length;
    if (this.mainSwiper && index >= 0 && index < mediaLength) {
      this.mainSwiper.slideTo(index);
    }
  }

  updateThumbsFade() {
    if (!this.thumbsSwiper || !this.thumbsSliderEl) return;

    // Check if swiper is at the beginning or end
    const isEnd = this.thumbsSwiper.isEnd;

    if (isEnd) {
      this.thumbsSliderEl.classList.remove("fade-right");
    } else {
      this.thumbsSliderEl.classList.add("fade-right");
    }
  }

  goToVariantImage(variantImage) {
    // Find the index of the variant image in the product media array
    const media =
      this.productMedia.length > 0 ? this.productMedia : this.productImages;
    const imageIndex = media.findIndex((item) => {
      const itemSrc = item.src || item;
      const image = typeof itemSrc === "string" ? itemSrc : null;
      if (!image) return false;

      // Compare image URLs (handle different sizes/formats)
      const variantImageId = variantImage.src
        .split("/")
        .pop()
        .split("?")[0]
        .split("_")[0];
      const productImageId = image.split("/").pop().split("?")[0].split("_")[0];
      return variantImageId === productImageId;
    });

    if (imageIndex !== -1) {
      this.goToSlide(imageIndex);
    }
  }

  updateAddToCartButton() {
    const translations = window.POPUP_TRANSLATIONS || {};
    const backInStockSection = this.querySelector(
      '.submit-button[data-slot="product-form-submit"]'
    );

    // Check if product is "auf_anfrage" - these never show as sold out
    const isAufAnfrage = this.metaData?.auf_anfrage;

    // Compute availability once (used for customizable and normal flow)
    let isAvailable = this.currentVariant?.available || false;
    if (this.metaData?.variants) {
      const variantMeta = this.metaData.variants.find(
        (v) => v.id === this.currentVariant?.id
      );
      if (variantMeta) {
        isAvailable = variantMeta.available && !variantMeta.disabled;
      }
    }

    // Check if product is customizable
    if (this.metaData?.is_customizable || this.metaData?.is_numerology) {
      this.addToCartBtn.style.display = "none";
      if (this.aufAnfrageFormEl) {
        this.aufAnfrageFormEl.classList.add("hidden");
      }
      if (isAvailable) {
        // In stock: show personalize as primary action, hide back-in-stock
        if (backInStockSection) {
          backInStockSection.classList.add("hidden");
        }
        if (this.detailsLink) {
          this.detailsLink.textContent =
            this.metaData.texts?.customize_button || "Anpassen";
          if (this.metaData?.is_numerology) {
            this.detailsLink.textContent =
              this.metaData.texts?.numerology_calculate_button;
          }
          this.detailsLink.classList.remove("btn-secondary");
          this.detailsLink.classList.add("btn-primary");
        }
      } else {
        // Sold out: show back-in-stock form; respect norestock
        if (this.norestockWrap) {
          this.norestockWrap.classList.add("hidden");
        }
        if (
          this.metaData?.norestock &&
          this.norestockWrap &&
          this.norestockLink
        ) {
          if (backInStockSection) {
            backInStockSection.classList.add("hidden");
          }
          const localeRoot =
            (typeof window !== "undefined" && window.Shopify.routes.root) || "";
          this.norestockLink.href =
            this.metaData.norestock_collection_url ||
            `${localeRoot}collections/all`;
          this.norestockLink.textContent =
            this.metaData.norestock_button_text || "View collection";
          this.norestockWrap.classList.remove("hidden");
        } else if (backInStockSection) {
          backInStockSection.classList.remove("hidden");
        }
        if (this.detailsLink) {
          this.detailsLink.textContent =
            this.metaData.texts?.details || "Details";
          this.detailsLink.classList.remove("btn-primary");
          this.detailsLink.classList.add("btn-secondary");
        }
      }
      return;
    }

    // Reset details link to secondary button for non-customizable products
    if (this.detailsLink) {
      this.detailsLink.textContent = this.metaData.texts?.details || "Details";
      this.detailsLink.classList.remove("btn-primary");
      this.detailsLink.classList.add("btn-secondary");
    }

    // If product is auf_anfrage, show the auf_anfrage form instead of add to cart/back in stock
    if (isAufAnfrage) {
      this.addToCartBtn.classList.add("hidden");
      if (backInStockSection) {
        backInStockSection.classList.add("hidden");
      }
      if (this.aufAnfrageFormEl) {
        this.aufAnfrageFormEl.classList.remove("hidden");
      }
      return;
    }

    // Hide auf_anfrage form for normal products
    if (this.aufAnfrageFormEl) {
      this.aufAnfrageFormEl.classList.add("hidden");
    }

    // Norestock: show link to parent collection instead of add-to-cart / back-in-stock
    if (this.norestockWrap) {
      this.norestockWrap.classList.add("hidden");
    }
    if (this.norestockLink && this.metaData?.norestock && !isAvailable) {
      const localeRoot =
        (typeof window !== "undefined" && window.Shopify.routes.root) || "";
      this.norestockLink.href =
        this.metaData.norestock_collection_url ||
        `${localeRoot}collections/all`;
      this.norestockLink.textContent =
        this.metaData.norestock_button_text || "View collection";
      this.norestockWrap?.classList.remove("hidden");
    }

    // Reset add to cart button display
    this.addToCartBtn.style.display = "";

    if (isAvailable) {
      this.addToCartBtn.classList.remove("popup-add-success", "btn-secondary");
      this.addToCartBtn.classList.add("btn-primary");
      this.addToCartBtn.disabled = false;
      this.addToCartBtn.textContent = translations.addToCart || "Add to Cart";
      this.addToCartBtn.classList.remove("hidden");
      if (backInStockSection) {
        backInStockSection.classList.add("hidden");
      }
    } else {
      this.addToCartBtn.classList.remove("popup-add-success", "btn-secondary");
      this.addToCartBtn.classList.add("btn-primary");
      this.addToCartBtn.disabled = true;
      this.addToCartBtn.textContent = translations.soldOut || "Sold Out";
      this.addToCartBtn.classList.add("hidden");
      if (this.metaData?.norestock) {
        if (backInStockSection) {
          backInStockSection.classList.add("hidden");
        }
      } else if (backInStockSection) {
        backInStockSection.classList.remove("hidden");
      }
    }
  }

  updateAvailability() {
    const availabilityEl = this.querySelector("#popup-availability");
    const stockStatusEl = this.querySelector("#popup-stock-status");
    const deliveryInfoEl = this.querySelector("#popup-delivery-info");

    if (!availabilityEl || !stockStatusEl || !deliveryInfoEl || !this.metaData)
      return;

    const variantMeta = this.metaData.variants?.find(
      (v) => v.id === this.currentVariant?.id
    );
    if (!variantMeta) return;

    const texts = this.metaData.texts || {};
    let stockText = "";
    let deliveryText = "";

    if (this.metaData.auf_anfrage) {
      availabilityEl.classList.add("hidden");
      return;
    }

    // Stock status
    if (variantMeta.available && !this.metaData.is_presale) {
      if (variantMeta.show_delivery) {
        if (
          variantMeta.inventory_policy === "continue" ||
          variantMeta.inventory_quantity > 5
        ) {
          stockText = texts.available || "Available";
        } else if (variantMeta.stock_level === "low") {
          stockText = texts.low_stock || "Low stock";
        } else if (variantMeta.show_delivery) {
          stockText = texts.available || "Available";
        }
      }
    }

    // Delivery info
    if (variantMeta.available) {
      if (variantMeta.has_delivery_time) {
        let unit = variantMeta.delivery_unit;
        if (unit === "Werktagen") {
          unit = texts.delivery_unit_days || "working days";
        } else if (unit === "Wochen") {
          unit = texts.delivery_unit_weeks || "weeks";
        }

        let deliveryTemplate =
          texts.delivery_pretext || "Delivery in {time} {unit}";
        deliveryText = deliveryTemplate
          .replace("{time}", variantMeta.delivery_time)
          .replace("{unit}", unit);
      } else if (!variantMeta.has_delivery_time) {
        deliveryText = texts.default_delivery || "Standard delivery time";
      }
    } else if (!variantMeta.available && this.metaData.norestock) {
      deliveryText = texts.no_stock_for_ever || "Unfortunately out of stock.";
    } else if (!variantMeta.available) {
      deliveryText = texts.no_stock || "Currently out of stock";
      if (variantMeta.release_date) {
        deliveryText += ` Voraussichtlich verfügbar ab ${variantMeta.release_date}`;
      }
    }

    stockStatusEl.textContent = stockText;
    deliveryInfoEl.textContent = deliveryText;

    // Show/hide availability section
    if (stockText || deliveryText) {
      availabilityEl.classList.remove("hidden");
    } else {
      availabilityEl.classList.add("hidden");
    }
  }

  updatePriceDisplay(price) {
    if (this.metaData?.noprice) {
      // Show anfrage_preis text instead of actual price
      const anfragePriceText =
        this.metaData.texts?.anfrage_preis || "Preis auf Anfrage";
      this.priceEl.textContent = anfragePriceText;
    } else if (
      this.metaData?.is_varying_price ||
      this.metaData?.is_customizable
    ) {
      // Show "Varying Price" text
      const varyingPriceText = this.metaData.texts?.varying_price;
      this.priceEl.textContent = varyingPriceText;
    } else {
      // Show formatted price without decimals
      const priceValue = Math.round(price / 100)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ".");
      this.priceEl.textContent = `€ ${priceValue}`;
    }
  }

  updatePriceRangeDisplay(product) {
    if (!this.priceRangeEl) return;

    /* Hide price range for auf_anfrage products */
    if (this.metaData?.auf_anfrage) {
      // Show anfrage_preis text instead of actual price
      const anfragePriceText =
        this.metaData.texts?.anfrage_preis || "Preis auf Anfrage";
      this.priceRangeEl.textContent = anfragePriceText;
      return;
    }
    if (this.metaData?.is_varying_price || this.metaData?.is_customizable) {
      // Show "Varying Price" text
      const varyingPriceText = this.metaData.texts?.varying_price;
      this.priceRangeEl.textContent = varyingPriceText;
      return;
    }

    const variantMetaById = new Map(
      (this.metaData?.variants || []).map((vm) => [vm.id, vm])
    );
    let variantsForPrice = product.variants.filter((v) => {
      const vm = variantMetaById.get(v.id);
      return !vm?.disabled;
    });
    if (variantsForPrice.length === 0) {
      variantsForPrice = product.variants;
    }
    const prices = variantsForPrice.map((v) => v.price);

    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);

    /* Only show range if min and max are different
    if (minPrice === maxPrice) {
      this.priceRangeEl.style.display = "none";
      return;
    }*/

    // Format prices without decimals
    const minFormatted = Math.round(minPrice / 100)
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    const maxFormatted = Math.round(maxPrice / 100)
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, ".");

    if (minFormatted === maxFormatted) {
      this.priceRangeEl.textContent = `€ ${minFormatted}`;
    } else {
      this.priceRangeEl.textContent = `€ ${minFormatted} – € ${maxFormatted}`;
    }
  }

  updateDetailsLink(productHandle, variantId) {
    const localeRoot =
      (typeof window !== "undefined" && window.Shopify.routes.root) || "";
    const productUrl = `${localeRoot}products/${productHandle}?variant=${variantId}`;
    if (this.detailsLink) {
      this.detailsLink.href = productUrl;
    }
    if (this.generalLink) {
      this.generalLink.href = productUrl;
    }
  }

  showLoading() {
    this.loadingEl.classList.remove("hidden");
    this.contentEl.classList.add("hidden");
    this.errorEl.classList.add("hidden");
  }

  showDetails() {
    this.loadingEl.classList.add("hidden");
    this.contentEl.classList.remove("hidden");
    this.errorEl.classList.add("hidden");
  }

  showError() {
    this.loadingEl.classList.add("hidden");
    this.contentEl.classList.add("hidden");
    this.errorEl.classList.remove("hidden");
  }

  open(productHandle, variantId = null) {
    if (this.isOpen) return;

    this.isOpen = true;
    this.classList.remove("hidden");
    this.classList.add("flex");
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", this.handleKeyDown);

    // Recalculate padding top: 58px + (40px - scrollDistance), no negative
    const scrollDistance = window.scrollY ?? window.pageYOffset;
    this.style.paddingTop = `${58 + Math.max(0, 40 - scrollDistance)}px`;

    if (productHandle) {
      this.loadProduct(productHandle, variantId);
    }

    // Focus trap
    setTimeout(() => {
      this.closeBtn?.focus();
    }, 100);
  }

  close() {
    if (!this.isOpen) return;

    this.isOpen = false;
    this.classList.remove("popup-gallery-light-overlay");
    this.classList.add("hidden");
    this.classList.remove("flex");
    this.style.paddingTop = ""; // Reset padding
    this.closeBtn.style.marginTop = ""; // Reset margin
    document.body.style.overflow = "";
    document.removeEventListener("keydown", this.handleKeyDown);

    // Pause all videos
    const allVideos = this.mainSliderEl?.querySelectorAll("video");
    allVideos?.forEach((video) => {
      video.pause();
    });

    // Reset all product state
    this.resetProductState();

    // Destroy Swiper instances to prevent memory leaks
    if (this.mainSwiper) {
      this.mainSwiper.destroy(true, true);
      this.mainSwiper = null;
    }

    if (this.thumbsSwiper) {
      this.thumbsSwiper.destroy(true, true);
      this.thumbsSwiper = null;
    }

    // Clear slider content so old images don't show when reopening with new product
    const mainWrapper = this.mainSliderEl?.querySelector(".swiper-wrapper");
    const thumbsWrapper = this.thumbsSliderEl?.querySelector(".swiper-wrapper");
    if (mainWrapper) mainWrapper.innerHTML = "";
    if (thumbsWrapper) thumbsWrapper.innerHTML = "";
  }
}

// Register the custom element
customElements.define("product-popup", ProductPopup);

// Global API for external apps
window.ProductPopup = {
  /**
   * Open the product popup for a specific product
   * @param {string} productHandle - The product handle/slug
   * @param {number} variantId - Optional variant ID to preset
   */
  open: (productHandle, variantId = null) => {
    const popup = document.querySelector("product-popup");
    if (popup) {
      popup.open(productHandle, variantId);
    } else {
      console.error("[ProductPopup] Popup element not found");
    }
  },

  /**
   * Close the product popup
   */
  close: () => {
    const popup = document.querySelector("product-popup");
    if (popup) {
      popup.close();
    }
  },

  /**
   * Check if popup is currently open
   */
  isOpen: () => {
    const popup = document.querySelector("product-popup");
    return popup ? popup.isOpen : false;
  },
};

// Log API availability
console.log("[ProductPopup] API available at window.ProductPopup");

export default ProductPopup;
